import { getURL } from "@/utils/helpers";
import {
  Provider,
  SignInWithPasswordCredentials,
  SignUpWithPasswordCredentials,
  SupabaseClient,
} from "@supabase/supabase-js";

import { Database } from "@/types/supabase";

export const createApiClient = (supabase: SupabaseClient<Database>) => {
  const passwordSignup = async (creds: SignUpWithPasswordCredentials) => {
    const { data, error } = await supabase.auth.signUp(creds);
    if (error) {
      if (error.message.toLowerCase().includes("user already registered")) {
        return { existingUser: true };
      }
      throw error;
    }
    if (data.user?.identities?.length === 0) {
      return { existingUser: true };
    }
    return { user: data.user, session: data.session };
  };
  const passwordSignin = async (creds: SignInWithPasswordCredentials) => {
    const res = await supabase.auth.signInWithPassword(creds);
    if (res.error) throw res.error;
    return res.data;
  };
  const passwordReset = async (email: string) => {
    const res = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: getURL("/api/reset_password"),
    });
    console.log("Password reset initiated:", res);
    if (res.error) throw res.error;
    return res.data;
  };
  const passwordUpdate = async (password: string) => {
    const res = await supabase.auth.updateUser({ password });
    if (res.error) throw res.error;
    return res.data;
  };
  const oauthSignin = async (provider: "google" | "github" | "twitter") => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: provider,
      options: {
        redirectTo: `${getURL()}/api/auth_callback`,
      },
    });
    if (error) throw error;
    return data;
  };
  const signOut = async () => {
    const res = await supabase.auth.signOut();
    if (res.error) throw res.error;
    return res;
  };

  const signInWithMagicLink = async (email: string) => {
    const res = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: getURL("/api/auth_callback"),
      },
    });
    if (res.error) {
      console.error("Magic link error:", res.error);
      throw res.error;
    }
    console.log("Magic link sent successfully:", res.data);
    return res.data;
  };

  const verifyEmail = async (token: string, email: string) => {
    const res = await supabase.auth.verifyOtp({
      token,
      type: "email",
      email,
    });
    if (res.error) throw res.error;
    return res.data;
  };

  const updateEmail = async (newEmail: string) => {
    const res = await supabase.auth.updateUser({ email: newEmail });
    if (res.error) throw res.error;
    return res.data;
  };

  const updateName = async (fullName: string) => {
    const res = await supabase.auth.updateUser({
      data: { full_name: fullName },
    });
    if (res.error) throw res.error;
    return res.data;
  };

  return {
    passwordSignin,
    passwordSignup,
    passwordReset,
    passwordUpdate,
    oauthSignin,
    signOut,
    signInWithMagicLink,
    verifyEmail,
    updateEmail,
    updateName,
  };
};
